import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import type { Program } from '@/types/api'
import { cn } from '@/lib/utils'

interface Props {
  programs: Program[]
  compact?: boolean
}

export function ProgramsGrid({ programs, compact }: Props) {
  const list = compact ? programs.slice(0, 6) : programs
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {list.map((p, i) => (
        <motion.div
          key={p.slug}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ delay: (i % 4) * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <ProgramCard program={p} index={i + 1} />
        </motion.div>
      ))}
    </div>
  )
}

function ProgramCard({ program, index }: { program: Program; index: number }) {
  return (
    <Link
      to={`/programs/${program.slug}`}
      className={cn(
        'group relative flex flex-col gap-3 rounded-2xl border overflow-hidden transition-all duration-500',
        'hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-15px_rgba(28,168,74,0.35)] hover:border-cosmos-green/50',
        program.featured
          ? 'bg-pitch-900 text-white border-pitch-900'
          : 'bg-card text-foreground border-border'
      )}
    >
      {/* Image thumbnail */}
      <div className="aspect-[16/10] overflow-hidden relative bg-pitch-800">
        <img
          src={program.image}
          alt={program.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-pitch-950/85 via-pitch-950/20 to-transparent" />
        <div
          className={cn(
            'absolute top-3 left-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest',
            program.featured ? 'bg-cosmos-gold text-pitch-950' : 'bg-cosmos-green text-white'
          )}
        >
          {String(index).padStart(2, '0')} · {program.ageRange}
        </div>
        {program.featured && (
          <div className="absolute top-3 right-3 rounded-full bg-white/95 text-pitch-950 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest">
            Featured
          </div>
        )}
      </div>

      {/* Gradient bar that draws on hover */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100',
          program.featured ? 'bg-cosmos-gold' : 'bg-gradient-to-r from-cosmos-green to-cosmos-gold'
        )}
      />

      {/* Content */}
      <div className="px-5 pb-5 pt-1 flex flex-col gap-2.5 flex-1">
        <h3 className="font-display text-[26px] leading-none uppercase tracking-tight">{program.name}</h3>
        <p
          className={cn(
            'text-sm leading-relaxed flex-1',
            program.featured ? 'text-white/78' : 'text-muted-foreground'
          )}
        >
          {program.shortDesc}
        </p>
        <div
          className={cn(
            'mt-2 flex items-center justify-between pt-3 border-t',
            program.featured ? 'border-white/12' : 'border-border'
          )}
        >
          <span
            className={cn(
              'text-[11px] font-bold uppercase tracking-widest',
              program.featured ? 'text-cosmos-gold' : 'text-cosmos-green'
            )}
          >
            Explore →
          </span>
          <div
            className={cn(
              'grid h-8 w-8 place-items-center rounded-full transition-all duration-500 group-hover:rotate-45',
              program.featured
                ? 'bg-cosmos-gold text-pitch-950'
                : 'bg-cosmos-green/12 text-cosmos-green group-hover:bg-cosmos-green group-hover:text-white'
            )}
          >
            <ArrowUpRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </Link>
  )
}
